import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState, type ChangeEvent } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CardActions,
  Box,
  Stack,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { PageContainer } from "src/components/page-container";
import { usePageView } from "src/hooks/use-page-view";
import {
  tutorialVideos,
  tutorialCategories,
  tutorialCategoryMap,
  type TutorialCategory,
  type TutorialVideo,
} from "src/constants/tutorials";
import { TutorialVideoButton } from "src/components/tutorial/tutorial-video-button";

export const Route = createFileRoute("/_authenticated/tutoriais")({
  component: () => <Page />,
});

const normalizeText = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

const tokenize = (value: string): string[] =>
  normalizeText(value)
    .split(/\s+/u)
    .filter((token) => token.length > 0);

const collectVideoTokens = (video: TutorialVideo): string[] => {
  const directTokens = [video.title, video.description];
  const keywordTokens = video.keywords;
  const categoryTokens = video.categories.flatMap((category) => {
    const categoryDefinition = tutorialCategoryMap.get(category);

    if (!categoryDefinition) {
      return [category];
    }

    return [categoryDefinition.label, ...categoryDefinition.keywords, category];
  });

  return [...directTokens, ...keywordTokens, ...categoryTokens]
    .map((token) => tokenize(token))
    .flat();
};

const getLevenshteinDistance = (source: string, target: string): number => {
  if (source === target) {
    return 0;
  }

  const sourceLength = source.length;
  const targetLength = target.length;

  if (sourceLength === 0) {
    return targetLength;
  }

  if (targetLength === 0) {
    return sourceLength;
  }

  const matrix: number[][] = Array.from({ length: sourceLength + 1 }, () =>
    Array.from({ length: targetLength + 1 }, () => 0),
  );

  for (let i = 0; i <= sourceLength; i += 1) {
    matrix[i][0] = i;
  }

  for (let j = 0; j <= targetLength; j += 1) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= sourceLength; i += 1) {
    for (let j = 1; j <= targetLength; j += 1) {
      const substitutionCost = source[i - 1] === target[j - 1] ? 0 : 1;

      const deletion = matrix[i - 1][j] + 1;
      const insertion = matrix[i][j - 1] + 1;
      const substitution = matrix[i - 1][j - 1] + substitutionCost;

      matrix[i][j] = Math.min(deletion, insertion, substitution);
    }
  }

  return matrix[sourceLength][targetLength];
};

const calculateTokenSimilarity = (queryToken: string, targetToken: string): number => {
  if (targetToken.includes(queryToken)) {
    const coverage = queryToken.length / targetToken.length;
    return 0.7 + coverage * 0.3;
  }

  const distance = getLevenshteinDistance(queryToken, targetToken);
  const maxLength = Math.max(queryToken.length, targetToken.length);

  if (maxLength === 0) {
    return 0;
  }

  const similarity = 1 - distance / maxLength;

  if (similarity < 0) {
    return 0;
  }

  return similarity * 0.7;
};

const calculateVideoScore = (query: string, video: TutorialVideo): number => {
  const tokens = collectVideoTokens(video);
  const queryTokens = tokenize(query);

  if (queryTokens.length === 0) {
    return 1;
  }

  let cumulativeScore = 0;
  let matchedTokens = 0;

  queryTokens.forEach((queryToken) => {
    let bestSimilarity = 0;

    tokens.forEach((token) => {
      const similarity = calculateTokenSimilarity(queryToken, token);

      if (similarity > bestSimilarity) {
        bestSimilarity = similarity;
      }
    });

    if (bestSimilarity > 0.4) {
      matchedTokens += 1;
    }

    cumulativeScore += bestSimilarity;
  });

  if (matchedTokens === 0) {
    return 0;
  }

  return cumulativeScore / queryTokens.length + matchedTokens / queryTokens.length;
};

interface TutorialCategoryFilter {
  value: TutorialCategory | "all";
  label: string;
}

const tutorialCategoryFilters: TutorialCategoryFilter[] = [
  { value: "all", label: "Todos" },
  ...tutorialCategories.map((category) => ({
    value: category.value,
    label: category.label,
  })),
];

const Page = () => {
  usePageView();

  const [selectedCategory, setSelectedCategory] = useState<TutorialCategory | "all">("all");
  const [searchTerm, setSearchTerm] = useState("");

  const handleCategorySelect = (value: TutorialCategory | "all") => {
    setSelectedCategory(value);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const filteredVideos = useMemo(() => {
    const normalizedQuery = searchTerm.trim();
    const categoryFiltered =
      selectedCategory === "all"
        ? tutorialVideos
        : tutorialVideos.filter((video) => video.categories.includes(selectedCategory));

    if (normalizedQuery === "") {
      return categoryFiltered;
    }

    return categoryFiltered
      .map((video) => ({
        video,
        score: calculateVideoScore(normalizedQuery, video),
      }))
      .filter((item) => item.score > 0.3)
      .sort((first, second) => second.score - first.score)
      .map((item) => item.video);
  }, [searchTerm, selectedCategory]);

  return (
    <PageContainer
      title="Tutoriais Thumbz"
      subtitle="Assista aos vídeos e aprenda a utilizar os principais recursos da plataforma."
    >
      <Stack spacing={4}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 2, md: 3 }}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ rowGap: 1, flexGrow: 1 }}
          >
            {tutorialCategoryFilters.map((category) => {
              const isSelected = selectedCategory === category.value;

              return (
                <Chip
                  key={category.value}
                  label={category.label}
                  clickable
                  onClick={() => handleCategorySelect(category.value)}
                  color={isSelected ? "primary" : "default"}
                  variant={isSelected ? "filled" : "outlined"}
                  size="small"
                />
              );
            })}
          </Stack>
          <TextField
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Busque por título, categoria ou ação"
            fullWidth
            size="small"
            variant="outlined"
            sx={{
              alignSelf: { xs: "stretch", md: "center" },
              width: { xs: "100%", md: 320 },
              flexShrink: 0,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
        {filteredVideos.length > 0 ? (
          <Box
            display="grid"
            gap={3}
            gridTemplateColumns={{
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            }}
          >
            {filteredVideos.map((video) => (
              <Card
                key={video.id}
                sx={{ display: "flex", flexDirection: "column", height: "100%" }}
              >
                <Box
                  sx={{
                    position: "relative",
                    pt: "56.25%",
                    width: "100%",
                  }}
                >
                  <Box
                    component="iframe"
                    src={`${video.embedUrl}?autoplay=0`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={video.title}
                    sx={{
                      border: 0,
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8,
                    }}
                  />
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack spacing={1.5}>
                    <Typography variant="h6">{video.title}</Typography>
                    <Typography color="text.secondary">{video.description}</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {video.categories.map((category) => {
                        const categoryDefinition = tutorialCategoryMap.get(category);
                        const label = categoryDefinition ? categoryDefinition.label : category;

                        return (
                          <Chip
                            key={category}
                            label={label}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        );
                      })}
                    </Stack>
                  </Stack>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <TutorialVideoButton
                    video={video}
                    buttonLabel="Assistir"
                    buttonProps={{ variant: "contained", fullWidth: true }}
                  />
                </CardActions>
              </Card>
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary">
            Nenhum tutorial corresponde à sua busca. Ajuste os filtros para encontrar o vídeo ideal.
          </Typography>
        )}
      </Stack>
    </PageContainer>
  );
};

export default Page;
